import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import { DarkModeToggle } from "../components/DarkModeToggle";
import { MobileDialog, MobilePopover } from "../components/mobile";
import { Z_CLASS } from "../components/mobile/zTiers";
import { Button } from "../components/ui/button";
import { UpgradePrompt } from "../components/UpgradePrompt";
import { Progress } from "../components/ui/progress";
import CoreJourneyMantra from "../components/CoreJourneyMantra";
import WelcomeTour from "../components/WelcomeTour";
import { ConfirmFocusCard } from "../components/ConfirmFocusCard";
import WeekShapeStrip from "../components/instructor/WeekShapeStrip";
import axios from "axios";
import { formatApiError } from "../utils/apiError";
import {
  Play,
  Lock,
  BookOpen,
  ChevronRight,
  LogOut,
  Settings,
  Bell,
  Car,
  Home,
  Rocket,
  Navigation,
  ClipboardCheck,
  Brain,
  Heart,
  GraduationCap,
  Sparkles,
  Target,
  Briefcase,
  ClipboardList,
  MessageCircle,
  TrendingUp,
  Star,
  CheckCircle,
  User,
  Sun,
  Flame,
  Award,
  Shield,
  HelpCircle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for sections
const iconMap = {
  Home: Home,
  Rocket: Rocket,
  Car: Car,
  Navigation: Navigation,
  ClipboardCheck: ClipboardCheck,
  Brain: Brain,
  Heart: Heart,
  GraduationCap: GraduationCap,
  Award: Award,
};

export default function Dashboard() {
  const { user, logout, refreshUser, hasActiveSubscription, needsAssessment } = useAuth();
  const { canAccess, isPaid } = useAccess();
  const navigate = useNavigate();
  const [progressData, setProgressData] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [showNotification, setShowNotification] = useState(true);
  const [showAssessmentPrompt, setShowAssessmentPrompt] = useState(true);
  const [pupilData, setPupilData] = useState(null);
  const [checkinData, setCheckinData] = useState(null);
  const [stageUnlockDismissed, setStageUnlockDismissed] = useState(false);
  const [redButton, setRedButton] = useState(null);
  const [showRedOnboarding, setShowRedOnboarding] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [adaptiveMessage, setAdaptiveMessage] = useState(null);
  const [adminViewRole, setAdminViewRole] = useState(user?.admin_view_role || "admin");
  const [welcomeReplay, setWelcomeReplay] = useState(false);

  const isAdmin = user?.is_admin || user?.role === "admin";
  const effectiveRole = isAdmin ? adminViewRole : user?.role;
  const isSubscribed = hasActiveSubscription();
  const showAssessment = needsAssessment() && showAssessmentPrompt;
  const isLearner = effectiveRole === "learner";
  const isInstructor = effectiveRole === "adi" || effectiveRole === "pdi";
  const isFreeUser = isLearner && !isSubscribed;

  useEffect(() => {
    fetchData();
  }, [user]);

  const switchAdminView = async (role) => {
    setAdminViewRole(role);
    try {
      await axios.put(`${API_URL}/api/admin/view-role`, { view_role: role }, { withCredentials: true });
      await refreshUser();
    } catch {}
    if (role === "adi") {
      navigate("/instructor", { replace: true });
      return;
    }
  };

  // Redirect instructors to their dedicated dashboard
  useEffect(() => {
    if (!isAdmin && (user?.role === "adi" || user?.role === "pdi")) {
      navigate("/instructor", { replace: true });
    }
  }, [user]);

  // Redirect to onboarding if learner hasn't seen it (skip for admin viewing as learner
  // OR for learners who arrived via a trusted invite — operator doctrine May 26, 2026).
  useEffect(() => {
    if (isLearner && !isAdmin && user && !user.learner_onboarding_seen && user.trusted_entry !== true) {
      navigate("/onboarding", { replace: true });
    }
  }, [user, isLearner]);

  // Redirect to check-in if learner hasn't done it today (skip for admin
  // AND for trusted-entry learners — no nag on day one for invited pupils).
  useEffect(() => {
    if (
      isLearner &&
      !isAdmin &&
      checkinData &&
      !checkinData.completed_today &&
      user?.learner_onboarding_seen !== false &&
      user?.trusted_entry !== true
    ) {
      navigate("/daily-checkin", { replace: true });
    }
  }, [checkinData, isLearner, user]);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API_URL}/api/progress/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/sections`, { withCredentials: true }),
      ];
      if (isLearner) {
        requests.push(
          axios.get(`${API_URL}/api/work-mode/pupil/dashboard`, { withCredentials: true }).catch(() => ({ data: { linked: false } }))
        );
        requests.push(
          axios.get(`${API_URL}/api/checkin/today`, { withCredentials: true }).catch(() => ({ data: { completed_today: true } }))
        );
        requests.push(
          axios.get(`${API_URL}/api/red-button/today`, { withCredentials: true }).catch(() => ({ data: { active: false, onboarding_seen: true } }))
        );
      }
      const results = await Promise.all(requests);
      setProgressData(results[0].data);
      setSections(results[1].data.filter(s => s.slug !== "home"));
      if (isLearner && results[2]) {
        setPupilData(results[2].data);
      }
      if (isLearner && results[3]) {
        setCheckinData(results[3].data);
      }
      if (isLearner && results[4]) {
        setRedButton(results[4].data);
      }
      // Fetch notifications
      if (isLearner) {
        try {
          const notifRes = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
          setNotifications(notifRes.data.notifications || []);
        } catch {}
        try {
          const adaptiveRes = await axios.get(`${API_URL}/api/adaptive/message`, { withCredentials: true });
          setAdaptiveMessage(adaptiveRes.data);
        } catch {}
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribeLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/subscribe`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data.founder_mode || data.no_charge) {
        // Admin/founder bypass — never redirect; show the message instead.
        alert(data.message || "Admin billing bypass active — use a real test account to test Stripe checkout.");
        return;
      }
      if (data.url) window.location.href = data.url;
    } catch (error) {
      console.error("Subscribe error:", error);
      alert(formatApiError(error, "Unable to start subscription. Please try again."));
    } finally {
      setSubscribeLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "learner": return "Learner Driver";
      case "pdi": return "PDI Trainee";
      case "adi": return "ADI Instructor";
      case "admin": return "Administrator";
      default: return role;
    }
  };

  const getSubscriptionPrice = () => {
    if (user?.plan === "legacy") return "£5/month (Legacy)";
    if (user?.role === "learner") return "£9.99/month";
    if (user?.plan === "starter") return "£19/month";
    if (user?.plan === "pro") return "£39/month";
    if (user?.plan === "elite") return "£59/month";
    return "See Plans";
  };

  const getPlanLabel = () => {
    const labels = { free: "Free Plan", legacy: "Legacy Plan", premium: "Premium", starter: "Starter", pro: "Pro", elite: "Elite", none: "No Plan" };
    return labels[user?.plan] || "Free";
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-20 sm:pb-0">
      {/* Header */}
      <header className={`bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 sticky top-0 ${Z_CLASS.CHROME}`}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div className="hidden sm:block">
              <p className="font-bold text-slate-900">National Driving Guild</p>
              <p className="text-xs text-slate-500">Welcome, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            {isLearner && (
              <MobilePopover open={showNotifPanel} onOpenChange={setShowNotifPanel}>
                <MobilePopover.Trigger asChild>
                  <Button
                    data-testid="notification-bell"
                    variant="ghost"
                    size="sm"
                    className="text-slate-600 relative"
                  >
                    <Bell className="w-5 h-5" />
                    {notifications.length > 0 && (
                      <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{notifications.length}</span>
                    )}
                  </Button>
                </MobilePopover.Trigger>
                <MobilePopover.Content
                  bareContent
                  testid="notification-panel"
                  side="bottom"
                  align="end"
                  sideOffset={8}
                >
                  <div className="w-72 bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden flex flex-col max-h-[inherit]">
                    <div className="p-3 border-b border-slate-100 flex-none">
                      <p className="text-xs text-slate-500 font-medium">Notifications</p>
                    </div>
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center">
                        <p className="text-xs text-slate-400">You're all caught up.</p>
                      </div>
                    ) : (
                      <div className="flex-1 overflow-y-auto overscroll-contain">
                        {notifications.map((n, i) => (
                          <button
                            key={`${n.id}-${i}`}
                            data-testid={`notif-${n.id}`}
                            onClick={() => { setShowNotifPanel(false); if (n.action) navigate(n.action); }}
                            className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-0"
                          >
                            <p className="text-sm text-slate-700 leading-relaxed">{n.message}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </MobilePopover.Content>
              </MobilePopover>
            )}
            <Link to="/learn/styles">
              <Button
                data-testid="preferences-btn"
                variant="ghost"
                size="sm"
                className="text-slate-600"
                title="How I Learn"
              >
                <Heart className="w-5 h-5" />
              </Button>
            </Link>
            {user?.role === "admin" && (
              <Link to="/admin">
                <Button
                  data-testid="admin-panel-btn"
                  variant="ghost"
                  size="sm"
                  className="text-slate-600"
                >
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
            )}
            <DarkModeToggle />
            <Button
              data-testid="welcome-tour-replay"
              variant="ghost"
              size="sm"
              onClick={async () => {
                try {
                  await axios.post(
                    `${API_URL}/api/onboarding/welcome/reset`,
                    {},
                    { withCredentials: true },
                  );
                } catch { /* best-effort */ }
                setWelcomeReplay(true);
              }}
              className="text-slate-600 dark:text-slate-400"
              title="Replay welcome tour"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-600 dark:text-slate-400"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome Tour — first-time + replay-from-settings */}
        <WelcomeTour
          forceOpen={welcomeReplay}
          onClose={() => setWelcomeReplay(false)}
        />

        {/* Confirm-focus card — surfaces upcoming-booking focus for learners */}
        {isLearner && <ConfirmFocusCard />}

        {/* Welcome Card */}
        <div
          data-testid="welcome-card"
          className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">
                Welcome back, {user?.name?.split(" ")[0]}!
              </h1>
              {isAdmin ? (
                <div className="flex items-center gap-2 mt-2">
                  {["admin", "adi", "learner"].map((r) => (
                    <button
                      key={r}
                      data-testid={`role-toggle-${r}`}
                      onClick={() => switchAdminView(r)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        adminViewRole === r
                          ? "bg-slate-900 text-white"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                    >
                      {r === "admin" ? "Admin" : r === "adi" ? "ADI View" : "Learner View"}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">
                  <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-sm font-medium">
                    {getRoleLabel(user?.role)}
                  </span>
                </p>
              )}
            </div>
            {isSubscribed && !isAdmin && (
              <div className="text-right hidden sm:block">
                <p className="text-xs text-slate-500 uppercase tracking-wide">{getPlanLabel()}</p>
                <p className="text-emerald-600 font-semibold">Active</p>
              </div>
            )}
            {!isSubscribed && !isAdmin && user?.role !== "admin" && (
              <div className="text-right hidden sm:block">
                <p className="text-xs text-slate-500 uppercase tracking-wide">{getPlanLabel()}</p>
              </div>
            )}
          </div>
        </div>

        {/* Work Mode Card (for ADI/PDI instructors or admin in ADI view) */}
        {(isInstructor || (isAdmin && adminViewRole === "adi")) && (user?.onboarding_complete || isAdmin) && (
          <div
            data-testid="work-mode-card"
            onClick={() => navigate("/work-mode")}
            className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 text-white cursor-pointer hover:from-slate-700 hover:to-slate-800 transition-all border border-slate-700"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-emerald-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-7 h-7 text-emerald-400" />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-xl mb-1">Work Mode</h2>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Start your lesson flow. Arrive, teach, release, reset.
                </p>
              </div>
              <ChevronRight className="w-6 h-6 text-slate-500 flex-shrink-0 mt-1" />
            </div>
          </div>
        )}

        {/* Growth Engine 2b — Week-Shape Visibility (Feb 17, 2026).
            Read-only operational X-ray of the instructor's week.
            Calm-baseline silence when the API returns no
            observations OR when prompt_level=never. Governed by
            /app/memory/GROWTH_DOCTRINE.md. */}
        {(isInstructor || (isAdmin && adminViewRole === "adi")) && (user?.onboarding_complete || isAdmin) && (
          <WeekShapeStrip />
        )}

        {/* First-time Instructor Onboarding Card */}
        {(user?.role === "adi" || user?.role === "pdi") && !user?.onboarding_complete && (
          <div
            data-testid="onboarding-card"
            className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 text-white border border-blue-500/30"
          >
            <h2 className="font-bold text-xl mb-2">Let's get you started</h2>
            <p className="text-blue-100 text-sm leading-relaxed mb-5">
              Two quick steps and you'll be running your first lesson.
            </p>
            <div className="space-y-3 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                <p className="text-sm text-blue-50">Add a pupil</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                <p className="text-sm text-blue-50">Run your first lesson</p>
              </div>
            </div>
            <Button
              data-testid="get-started-btn"
              onClick={() => navigate("/work-mode")}
              className="w-full h-14 bg-white text-blue-700 hover:bg-blue-50 font-bold rounded-xl text-base"
            >
              Get Started
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        )}


        {/* My Lessons Card (for learners) */}
        {isLearner && (
          <div
            data-testid="my-lessons-card"
            onClick={() => navigate("/passport")}
            className="bg-white rounded-2xl border border-slate-200 p-5 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all flex items-center gap-4"
          >
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-7 h-7 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-slate-900">My Lessons</h2>
              <p className="text-slate-500 text-sm">
                View your lesson history, feedback & progress
              </p>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-400 flex-shrink-0" />
          </div>
        )}

        {/* ===== LEARNER DASHBOARD ===== */}

        {/* Booking Urgency Card — first thing learners see */}
        {isLearner && pupilData?.linked && !pupilData?.has_upcoming_booking && (
          <div data-testid="booking-urgency-card" className="bg-slate-900 rounded-2xl p-5 text-white">
            <h2 className="text-lg font-bold mb-2">Secure your lesson slots</h2>
            <p className="text-sm text-slate-300 leading-relaxed mb-1">
              Book early so nobody else takes your preferred times.
            </p>
            <p className="text-xs text-slate-400 mb-4">
              No payment needed today — you pay 3 days before each lesson.
            </p>
            <Button
              data-testid="book-lessons-btn"
              onClick={() => navigate("/learn/book")}
              className="w-full h-11 bg-white text-slate-900 hover:bg-slate-100 font-bold rounded-xl text-sm"
            >
              Book Lessons Now
            </Button>
          </div>
        )}

        {/* Stage Unlock Message (overlay — shown once) */}
        {isLearner && checkinData && checkinData.stage_unlock_pending && !stageUnlockDismissed && (
          <div data-testid="stage-unlock-card" className="bg-slate-900 rounded-2xl p-6 text-white">
            {checkinData.stage_unlock_pending === 2 && (
              <div className="space-y-3">
                <p className="text-xs text-slate-400 uppercase tracking-wide font-medium">Stage 2</p>
                <h2 className="text-lg font-bold">Control</h2>
                <div className="space-y-2.5 text-slate-300 text-sm leading-relaxed">
                  <p>You are moving from awareness into control.</p>
                  <p>You are beginning to understand yourself and your driving more consistently.</p>
                  <p>Stay focused — control is built through repetition.</p>
                </div>
              </div>
            )}
            {checkinData.stage_unlock_pending === 3 && (
              <div className="space-y-3">
                <p className="text-xs text-slate-400 uppercase tracking-wide font-medium">Stage 3</p>
                <h2 className="text-lg font-bold">Mastery</h2>
                <div className="space-y-2.5 text-slate-300 text-sm leading-relaxed">
                  <p>You are now driving with control and awareness.</p>
                  <p>You understand yourself, the road, and the people around you.</p>
                  <p>You are not just ready to pass your test — you are ready to handle real driving.</p>
                </div>
              </div>
            )}
            <Button
              data-testid="dismiss-stage-unlock-btn"
              onClick={async () => {
                setStageUnlockDismissed(true);
                try { await axios.post(`${API_URL}/api/checkin/dismiss-stage-unlock`, {}, { withCredentials: true }); } catch {}
              }}
              className="w-full mt-5 bg-white text-slate-900 hover:bg-slate-100 font-bold rounded-xl h-11"
            >
              Continue
            </Button>
          </div>
        )}

        {/* 1. Today's Status */}
        {isLearner && (checkinData || redButton) && (
          <div data-testid="today-summary" className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs text-slate-400 uppercase tracking-wide font-medium mb-3">Today</p>

            {/* Adaptive personalised message */}
            {adaptiveMessage?.message ? (
              <div data-testid="adaptive-message" className="mb-3">
                <p className="text-sm text-slate-700 leading-relaxed">{adaptiveMessage.message}</p>
                {canAccess("full_personalisation") && adaptiveMessage.secondary?.message && (
                  <p data-testid="adaptive-secondary" className="text-xs text-slate-500 mt-2">{adaptiveMessage.secondary.message}</p>
                )}
              </div>
            ) : (
              <p data-testid="status-message" className="text-sm text-slate-700 leading-relaxed mb-3">
                {redButton?.active
                  ? "You've asked for a different approach today. Your instructor will adjust."
                  : !checkinData?.completed_today
                    ? "Start your check-in to get ready for today."
                    : "You're checked in and ready for today."}
              </p>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">Check-in</p>
                <span className={`text-xs font-semibold ${checkinData?.completed_today ? "text-emerald-600" : "text-slate-400"}`}>
                  {checkinData?.completed_today ? "Done" : "Not done"}
                </span>
              </div>
              {redButton?.active && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">Red Button Day</p>
                  <span className="text-xs font-semibold text-red-500">Active</span>
                </div>
              )}
            </div>

            {/* Check-in button */}
            {checkinData && !checkinData.completed_today && (
              <Button
                data-testid="open-checkin-btn"
                onClick={() => navigate("/daily-checkin")}
                className="w-full rounded-xl h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold mt-4"
              >
                Start Today's Check-In
              </Button>
            )}
          </div>
        )}

        {/* 2. Next Lesson Focus + Preparation */}
        {isLearner && pupilData?.next_lesson_focus && (() => {
          const focus = pupilData.next_lesson_focus;
          const FREE_PREP_TOPICS = ["Roundabouts", "Junctions", "Manoeuvres"];
          const isFreePrepLocked = !canAccess("full_lesson_prep") && !FREE_PREP_TOPICS.includes(focus);
          // Lesson prep videos suppressed until founder approves each clip.
          // Bullet points (vetted platform copy) remain visible. To re-enable
          // a video, replace `null` with the embed URL.
          const prepContent = {
            "Roundabouts": {
              video: null,
              points: ["Approach slowly", "Choose correct lane early", "Observe traffic flow", "Commit confidently when safe", "Stay calm and controlled"],
            },
            "Junctions": {
              video: null,
              points: ["Approach slowly and read the road", "Check both directions — look, look again", "Signal clearly and in good time", "Wait for a safe gap before moving"],
            },
            "Manoeuvres": {
              video: null,
              points: ["Keep the car slow with clutch control", "Check mirrors and blind spots throughout", "Accuracy matters more than speed", "If unsure, stop and start again"],
            },
            "Speed Control": {
              video: null,
              points: ["Read the road ahead before you react", "Brake gently and in good time", "Match your speed to the situation", "Stay aware of changing conditions"],
            },
            "Awareness & Planning": {
              video: null,
              points: ["Scan further ahead, not just the car in front", "Check mirrors before changing speed or direction", "Anticipate what others might do", "Plan your actions early"],
            },
          };
          const prep = prepContent[focus];
          if (isFreePrepLocked) {
            return (
              <div data-testid="lesson-prep-locked" className="bg-white rounded-2xl border border-slate-200 p-5">
                <p className="text-xs text-slate-400 uppercase tracking-wide font-medium mb-3">Next Lesson Focus</p>
                <p className="text-base font-bold text-slate-900 mb-3">{focus}</p>
                <p className="text-sm text-slate-500 mb-4">Preparation materials for this topic are available with a paid plan.</p>
                <UpgradePrompt variant="inline" context="Unlock all lesson preparation" />
              </div>
            );
          }
          return (
            <div data-testid="lesson-prep-card" className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-400 uppercase tracking-wide font-medium">Next Lesson Focus</p>
                <p className="text-[10px] text-slate-400">3 min</p>
              </div>
              <p className="text-base font-bold text-slate-900 mb-4">{focus}</p>

              {prep?.video && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 mb-2">Watch This First</p>
                  <div className="relative w-full rounded-xl overflow-hidden bg-slate-100" style={{paddingBottom: "56.25%"}}>
                    <iframe data-testid="prep-video" src={prep.video} title={`${focus} preparation`} className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  </div>
                </div>
              )}

              {prep && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 mb-2">What to Focus On</p>
                  <ul className="space-y-2">
                    {prep.points.map((p, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2"><span className="text-slate-400 mt-0.5">-</span>{p}</li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-xs text-slate-400 leading-relaxed mb-4">This will help you feel more confident and in control.</p>
              <Button
                data-testid="lesson-prep-ready-btn"
                onClick={async () => {
                  try { await axios.post(`${API_URL}/api/lesson-prep/ready`, {}, { withCredentials: true }); } catch {}
                  const el = document.querySelector('[data-testid="lesson-prep-card"]');
                  if (el) el.style.display = 'none';
                }}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl"
              >
                I'm Ready
              </Button>
            </div>
          );
        })()}

        {/* 3. Driver Progress */}
        {isLearner && checkinData && checkinData.stage && (
          <div data-testid="driver-progress-card" className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs text-slate-400 uppercase tracking-wide font-medium mb-1">Driver Progress</p>
            <p data-testid="stage-message" className="text-base font-semibold text-slate-900 mb-4">
              {checkinData.stage.stage === 3 ? "You are driving with mastery." : `You are building ${checkinData.stage.name.toLowerCase()}.`}
            </p>

            <CoreJourneyMantra variant="light" className="mb-4" testid="dashboard-progress-mantra" />

            <div className="flex items-center gap-2 mb-5">
              {["Awareness", "Control", "Mastery"].map((name, i) => {
                const stageNum = i + 1;
                const current = checkinData.stage?.stage;
                const isActive = stageNum === current;
                const isComplete = stageNum < current;
                return (
                  <div key={name} className="flex items-center flex-1 gap-2">
                    <div className="flex flex-col items-center flex-1">
                      <div data-testid={`stage-${stageNum}-marker`} className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 text-xs font-bold ${isActive || isComplete ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-400"}`}>{stageNum}</div>
                      <span className={`text-[10px] ${isActive ? "text-slate-900 font-semibold" : isComplete ? "text-slate-600" : "text-slate-400"}`}>{name}</span>
                    </div>
                    {i < 2 && <div className={`h-px w-full mb-4 ${stageNum < current ? "bg-slate-900" : "bg-slate-200"}`} style={{maxWidth: "1.5rem"}} />}
                  </div>
                );
              })}
            </div>

            {canAccess("full_progression") ? (
              <>
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-sm text-slate-700">{checkinData.points} points</span>
                  {checkinData.next_stage && <span className="text-xs text-slate-400">{checkinData.next_stage.min} to {checkinData.next_stage.name.toLowerCase()}</span>}
                </div>
                {checkinData.next_stage && (
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-slate-900 rounded-full transition-all" style={{ width: `${Math.round(((checkinData.points - checkinData.stage.min) / (checkinData.next_stage.min - checkinData.stage.min)) * 100)}%` }} />
                  </div>
                )}
                {checkinData.streak > 0 && <p className="text-xs text-slate-500 mt-3">{checkinData.streak} {checkinData.streak === 1 ? "day" : "days"} consistent</p>}
              </>
            ) : (
              <UpgradePrompt variant="inline" context="Unlock full progression tracking" />
            )}
          </div>
        )}

        {/* 4 & 5. Skill Progress + Overall */}
        {isLearner && pupilData?.skill_scores && Object.keys(pupilData.skill_scores).length > 0 && (() => {
          if (!canAccess("full_skill_progress")) {
            return (
              <div data-testid="skill-progress-locked" className="bg-white rounded-2xl border border-slate-200 p-5">
                <p className="text-xs text-slate-400 uppercase tracking-wide font-medium mb-3">Skill Progress</p>
                <p className="text-sm text-slate-500 mb-4">See how your driving skills are developing across all areas.</p>
                <UpgradePrompt variant="inline" context="Unlock full skill tracking" />
              </div>
            );
          }
          const scores = Object.values(pupilData.skill_scores);
          const overall = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
          return (
          <div data-testid="skill-progress-card" className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs text-slate-400 uppercase tracking-wide font-medium mb-5">Skill Progress</p>

            <div className="flex flex-col items-center mb-6">
              <div className="relative w-24 h-24 mb-2">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="42" fill="none" stroke="#f1f5f9" strokeWidth="6" />
                  <circle cx="48" cy="48" r="42" fill="none" stroke="#1e293b" strokeWidth="6" strokeLinecap="round" strokeDasharray={`${(overall / 100) * 263.9} 263.9`} />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-slate-900">{overall}%</span>
              </div>
              <p className="text-sm font-medium text-slate-900">Overall Driving Progress</p>
              {overall >= 80 && <p data-testid="test-readiness-msg" className="text-xs text-slate-500 mt-1">You are approaching test readiness.</p>}
            </div>

            <div className="grid grid-cols-3 gap-4">
              {Object.entries(pupilData.skill_scores).map(([skill, score]) => (
                <div key={skill} className="flex flex-col items-center">
                  <div className="relative w-16 h-16 mb-2">
                    <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                      <circle cx="32" cy="32" r="28" fill="none" stroke="#f1f5f9" strokeWidth="5" />
                      <circle cx="32" cy="32" r="28" fill="none" stroke={score >= 70 ? "#1e293b" : score >= 30 ? "#64748b" : "#cbd5e1"} strokeWidth="5" strokeLinecap="round" strokeDasharray={`${(score / 100) * 175.9} 175.9`} />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-900">{score}%</span>
                  </div>
                  <span className="text-[11px] text-slate-600 text-center leading-tight">{skill}</span>
                  {score >= 80 && <span data-testid={`test-ready-${skill.toLowerCase().replace(/\s+/g, "-")}`} className="text-[10px] font-semibold text-slate-900 mt-1">Test Ready</span>}
                </div>
              ))}
            </div>
          </div>
          );
        })()}

        {/* 6. Rewards */}
        {isLearner && pupilData?.rewards?.length > 0 && (() => {
          const available = pupilData.rewards.filter((r) => r.status === "unused");
          const used = pupilData.rewards.filter((r) => r.status === "used");
          return (
            <div data-testid="learner-rewards-card" className="bg-white rounded-2xl border border-slate-200 p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-medium mb-4">Your Rewards</p>
              {available.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 mb-2">Available</p>
                  <div className="space-y-2">
                    {available.map((r, i) => (
                      <div key={i} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-3.5 py-2.5">
                        <p className="text-sm font-medium text-emerald-800">{r.reward_label}</p>
                        <p className="text-[10px] text-emerald-600">{r.date_awarded ? new Date(r.date_awarded).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {used.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Used</p>
                  <div className="space-y-1.5">
                    {used.map((r, i) => (
                      <div key={i} className="flex items-center justify-between px-3.5 py-2">
                        <p className="text-sm text-slate-400 line-through">{r.reward_label}</p>
                        <p className="text-[10px] text-slate-400">{r.date_awarded ? new Date(r.date_awarded).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* 7. Reflection / Awareness Prompt */}
        {isLearner && checkinData && !adaptiveMessage?.message && (
          <div data-testid="awareness-prompt" className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-sm text-slate-600 leading-relaxed">
              {checkinData.streak >= 7
                ? "You've been showing good consistency. Keep it going."
                : checkinData.streak >= 3
                  ? "You're building a strong habit. Consistency leads to confidence."
                  : redButton?.active
                    ? "It's okay to need a different approach. Being honest is a strength."
                    : checkinData.completed_today
                      ? "Preparation is helping your confidence grow."
                      : "Every check-in builds awareness. Start today."}
            </p>
          </div>
        )}

        {/* Red Button Day */}
        {isLearner && redButton && !redButton.active && (
          <div data-testid="red-button-section" className="bg-white rounded-2xl border border-slate-200 p-5">
            <button
              data-testid="red-button-btn"
              onClick={async () => {
                if (!redButton.onboarding_seen) { setShowRedOnboarding(true); return; }
                try { await axios.post(`${API_URL}/api/red-button/activate`, {}, { withCredentials: true }); setRedButton({ ...redButton, active: true }); } catch {}
              }}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-red-200 hover:border-red-400 hover:bg-red-50 transition-colors text-left"
            >
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                <div className="w-6 h-6 bg-white rounded-full" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Red Button Day</p>
                <p className="text-xs text-slate-500 mt-0.5">Use this if you're not in the right headspace to be taught normally today.</p>
              </div>
            </button>
          </div>
        )}

        {/* Red Button Onboarding Modal */}
        {isLearner && showRedOnboarding && (
          <MobileDialog
            bareContent
            open={showRedOnboarding}
            onClose={() => setShowRedOnboarding(false)}
            testid="red-button-onboarding-wrap"
          >
            <div data-testid="red-button-onboarding" className="bg-white w-full max-w-sm rounded-t-3xl sm:rounded-3xl p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-3">Red Button Day</h2>
              <div className="space-y-3 text-sm text-slate-600 leading-relaxed mb-6">
                <p>Some days aren't normal.</p>
                <p>You might feel distracted, overwhelmed, stressed, or just not yourself.</p>
                <p>On those days, press the red button.</p>
                <p className="text-slate-800 font-medium">This tells your instructor: "Today isn't a normal learning day — I need a different approach."</p>
                <p>This isn't about avoiding learning.</p>
                <p>It's about being honest so you can still make progress in the right way.</p>
              </div>
              <Button data-testid="red-onboarding-dismiss" onClick={async () => { setShowRedOnboarding(false); setRedButton({ ...redButton, onboarding_seen: true }); try { await axios.post(`${API_URL}/api/red-button/dismiss-onboarding`, {}, { withCredentials: true }); } catch {} }} className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl">Got it</Button>
            </div>
          </MobileDialog>
        )}

        {/* Instructor Connection Card (for linked learners) */}
        {isLearner && pupilData?.linked && (
          <div
            data-testid="instructor-connection-card"
            className="bg-white rounded-2xl border border-blue-200 p-4 flex items-center gap-4"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Your Instructor</p>
              <p className="font-semibold text-slate-900">{pupilData.student?.instructor_name}</p>
            </div>
            <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">Connected</span>
          </div>
        )}

        {/* Latest Lesson Feedback (for linked learners with lessons) */}
        {isLearner && pupilData?.linked && pupilData?.lessons?.length > 0 && (
          <div data-testid="latest-feedback-card" className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4">
            <h2 className="font-bold text-sm text-slate-900 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-blue-500" />
              Latest Lesson Feedback
            </h2>
            {pupilData.lessons[0].feedback_summary && (
              <p className="text-sm text-slate-700 leading-relaxed">{pupilData.lessons[0].feedback_summary}</p>
            )}
            {pupilData.lessons[0].went_well?.length > 0 && (
              <div>
                <p className="text-xs text-emerald-600 font-medium mb-1.5">Went Well</p>
                <div className="flex flex-wrap gap-1.5">
                  {pupilData.lessons[0].went_well.slice(0, 4).map(s => (
                    <span key={s} className="bg-emerald-50 text-emerald-700 text-xs px-2.5 py-1 rounded-full border border-emerald-200">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {pupilData.lessons[0].needs_work?.length > 0 && (
              <div>
                <p className="text-xs text-amber-600 font-medium mb-1.5">To Practise</p>
                <div className="flex flex-wrap gap-1.5">
                  {pupilData.lessons[0].needs_work.slice(0, 4).map(s => (
                    <span key={s} className="bg-amber-50 text-amber-700 text-xs px-2.5 py-1 rounded-full border border-amber-200">{s}</span>
                  ))}
                </div>
              </div>
            )}
            <button
              onClick={() => navigate("/passport")}
              className="text-xs text-blue-600 font-medium hover:underline"
              data-testid="view-all-feedback-btn"
            >
              View all lessons &rarr;
            </button>
          </div>
        )}

        {/* Focus Areas (for linked learners) */}
        {isLearner && pupilData?.linked && pupilData?.progress?.current_focus?.length > 0 && (
          <div data-testid="focus-areas-card" className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
            <h2 className="font-bold text-sm text-blue-900 mb-3 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Focus for Next Lesson
            </h2>
            <ul className="space-y-2">
              {pupilData.progress.current_focus.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-blue-800">
                  <span className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-blue-700 mt-0.5">{i + 1}</span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Quick Progress Summary (for linked learners) */}
        {isLearner && pupilData?.linked && pupilData?.progress && (
          <div data-testid="pupil-progress-summary" className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
              <p className="text-2xl font-black text-blue-600">{pupilData.progress.total_lessons}</p>
              <p className="text-xs text-slate-500 mt-1">Lessons</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
              <p className="text-2xl font-black text-emerald-600">{pupilData.progress.strengths?.length || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Strengths</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
              <p className="text-2xl font-black text-amber-600">{pupilData.progress.areas_to_improve?.length || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Working On</p>
            </div>
          </div>
        )}

        {/* Linked learner with no lessons yet */}
        {isLearner && pupilData?.linked && (!pupilData?.lessons || pupilData.lessons.length === 0) && (
          <div data-testid="no-lessons-yet" className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Car className="w-6 h-6 text-blue-600" />
            </div>
            <p className="font-semibold text-slate-900 mb-1">Your instructor is ready</p>
            <p className="text-sm text-slate-500">After your first lesson, feedback and progress will appear here.</p>
          </div>
        )}

        {/* Upgrade prompt after value display (for free linked learners with lessons) */}
        {isFreeUser && pupilData?.linked && pupilData?.lessons?.length > 0 && (
          <UpgradePrompt variant="inline" context="Get more from your learning journey" />
        )}

        {/* Assessment Prompt Card (for learners who haven't taken it) */}
        {showAssessment && (
          <div
            data-testid="assessment-prompt-card"
            className="bg-white rounded-2xl border-2 border-blue-200 p-5"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-slate-900 mb-1">Find Your Starting Point</h2>
                <p className="text-slate-500 text-sm mb-4 leading-relaxed">
                  Quick 8-question assessment to see where you are and what to focus on.
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    data-testid="take-assessment-btn"
                    onClick={() => navigate("/assessment")}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 rounded-xl"
                  >
                    Start Assessment
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                  <button
                    onClick={() => setShowAssessmentPrompt(false)}
                    className="text-slate-400 hover:text-slate-600 text-sm"
                  >
                    Later
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assessment Completed — Return Hook Banner (if completed but not subscribed) */}
        {user?.assessment_completed && user?.assessment_result && isFreeUser && (
          <div
            data-testid="assessment-return-hook"
            onClick={() => navigate("/upgrade")}
            className="bg-gradient-to-r from-blue-50 to-slate-50 border border-blue-200 rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:border-blue-300 transition-all"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-slate-900 text-sm">Your personalised plan is ready</p>
              <p className="text-xs text-slate-500">Unlock it anytime</p>
            </div>
            <ChevronRight className="w-5 h-5 text-blue-400 flex-shrink-0" />
          </div>
        )}

        {/* Assessment Result Summary (if completed) */}
        {user?.assessment_completed && user?.assessment_result && !isFreeUser && (
          <div
            data-testid="assessment-result-card"
            className="bg-white rounded-2xl border border-slate-200 p-5"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Star className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Your Level</p>
                <p className="font-semibold text-slate-900">{user.assessment_result.level_name}</p>
              </div>
              <Button
                data-testid="retake-assessment-btn"
                size="sm"
                variant="outline"
                onClick={() => navigate("/assessment")}
                className="rounded-xl text-xs"
              >
                Retake
              </Button>
            </div>
          </div>
        )}

        {/* Notification Banner */}
        {showNotification && progressData?.next_lesson && isSubscribed && (
          <div
            data-testid="notification-banner"
            className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Continue your learning</p>
                <p className="text-slate-600 text-xs">{progressData.next_lesson.title}</p>
              </div>
            </div>
            <button
              onClick={() => setShowNotification(false)}
              className="text-slate-400 hover:text-slate-600 text-xl"
            >
              ×
            </button>
          </div>
        )}

        {/* Progress Card */}
        {isSubscribed && (
          <div
            data-testid="progress-card"
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg text-slate-900">Your Progress</h2>
              <span className="text-2xl font-black text-blue-700">
                {progressData?.percentage || 0}%
              </span>
            </div>
            <Progress
              value={progressData?.percentage || 0}
              className="h-3 bg-slate-100"
            />
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Completed</p>
                <p className="text-xl font-bold text-slate-900 mt-1">
                  {progressData?.completed_lessons || 0}/{progressData?.total_lessons || 0}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Sections</p>
                <p className="text-xl font-bold text-slate-900 mt-1">{sections.length}</p>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Card (for non-subscribers) */}
        {/* Membership routing fix (Feb 8, 2026): gate on isLearner so a
            PDI viewing /dashboard never sees the learner upgrade prompt
            mounted at all. UpgradePrompt itself is also role-aware now,
            but cleaner to not render learner-context cards on non-learner
            renders of this page. */}
        {isLearner && !isSubscribed && user?.role !== "admin" && !pupilData?.linked && (
          <UpgradePrompt variant="card" />
        )}

        {/* Quick Actions (for subscribers) */}
        {isSubscribed && (
          <div className="grid sm:grid-cols-2 gap-4">
            {progressData?.last_lesson && (
              <Link
                to={`/lesson/${progressData.last_lesson.id}`}
                data-testid="last-lesson-card"
                className="bg-white rounded-2xl border border-slate-200 p-5 card-hover flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Last Watched</p>
                  <p className="font-semibold text-slate-900 truncate">{progressData.last_lesson.title}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
              </Link>
            )}
            {progressData?.next_lesson && (
              <Link
                to={`/lesson/${progressData.next_lesson.id}`}
                data-testid="next-lesson-card"
                className="bg-white rounded-2xl border border-slate-200 p-5 card-hover flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Play className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Continue</p>
                  <p className="font-semibold text-slate-900 truncate">{progressData.next_lesson.title}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
              </Link>
            )}
          </div>
        )}

        {/* Content Sections */}
        <div>
          <h2 className="font-bold text-lg text-slate-900 mb-4">
            {isLearner ? "Your Learning Path" : "Content Sections"}
          </h2>
          {isFreeUser && (
            <p className="text-sm text-slate-500 mb-4">
              First 3 lessons free — upgrade anytime for full access
            </p>
          )}
          <div className="space-y-3">
            {sections.map((section, index) => {
              const IconComponent = iconMap[section.icon] || BookOpen;
              const isInstructorNoSub = !isSubscribed && !isAdmin && user?.role !== "admin" && !isLearner;
              
              return (
                <div
                  key={section.id}
                  data-testid={`section-card-${index}`}
                  onClick={() => !isInstructorNoSub && navigate(`/section/${section.slug}`)}
                  className={`bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-4 ${
                    isInstructorNoSub ? "opacity-75" : "card-hover cursor-pointer"
                  }`}
                >
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 flex items-center justify-center">
                    {section.thumbnail_url ? (
                      <img
                        src={section.thumbnail_url}
                        alt={section.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <IconComponent className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">{section.title}</p>
                    <p className="text-sm text-slate-500 truncate">{section.description}</p>
                  </div>
                  {isInstructorNoSub ? (
                    <Lock className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {sections.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {user?.role === "learner" 
                ? "Content is being prepared for you"
                : "Content for your role is coming soon"}
            </p>
          </div>
        )}
      </div>

      {/* Mobile Bottom Nav */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 pb-safe mobile-nav">
        <div className="flex items-center justify-around">
          <Link
            to="/dashboard"
            className="flex flex-col items-center gap-1 text-blue-600"
            data-testid="mobile-nav-dashboard"
          >
            <Home className="w-6 h-6" />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <button
            onClick={handleLogout}
            className="flex flex-col items-center gap-1 text-slate-500"
            data-testid="mobile-nav-logout"
          >
            <LogOut className="w-6 h-6" />
            <span className="text-xs font-medium">Logout</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
